import _ from 'lodash';
import {
  taxRateValues,
  expensesValues,
  specialExpensesValues,
  timeFrameForSpecialExpensesValues,
  liquidityNeedsValues,
  riskToleranceValues,
  investmentObjectivesValues,
  yearsOfExperience,
  transactionsPerYear,
  investmentOptions,
  investmentTimespanOptions,
  decisionMakingOptions,
} from '../../components/Forms/Schemas/baseRrf';

const fundingMethodsMappings = {
  income: 'income',
  pension: 'pension_or_retirement_savings',
  otherFunds: 'funds_from_other_account',
  gift: 'gift',
  saleOfBusiness: 'sale',
  insurancePayout: 'insurance_payout',
  inheritance: 'inheritance',
  socialSecurityBenefits: 'social_security_benefits',
  reverseMortgage: 'home_equity',
};

/**
 * Enum to differentiate between mappings
 */
const ConverterOptions = Object.freeze({ TOVIEWMODEL: 'toViewModel', TODTO: 'toDto' });

/**
 * Value to hold the mapping convertion options
 */
let converterOption;

/**
 * One way mapper method handling key to key mappings
 * @param {*} iterateableValues The iteratable values containing the keys
 * @param {*} searchItem The item to search by
 */
const getLinearValueFromKeyIteration = (iterateableValues, searchItem) => {
  const indexOfValue = Object.keys(iterateableValues).indexOf(searchItem);
  if (indexOfValue >= 0) {
    return Object.keys(iterateableValues)[indexOfValue];
  }
  return undefined;
};

/**
 * Two way mapper method handling the conditional tuplet values {years of experience, transactions per year}
 * @param {*} key The key denoting if the touplet has value
 * @param {*} values The touplet base values containing the pair's values
 */
const getConditionalTupletValue = (key, values) => ({ body = undefined, dtoKeyName = undefined }) => {
  switch (converterOption) {
    case ConverterOptions.TOVIEWMODEL: {
      let yearsOfExperienceMapped;
      if (!_.isEmpty(values)) {
        yearsOfExperienceMapped = values.yearsOfExperience === '0' ? 'ZERO' : values.yearsOfExperience;
      }
      return {
        hasValue: !!key,
        yearsOfExperience: _.isEmpty(values) ? undefined : getLinearValueFromKeyIteration(yearsOfExperience, yearsOfExperienceMapped),
        transactionsPerYear: _.isEmpty(values) ? undefined : getLinearValueFromKeyIteration(transactionsPerYear, values.transactionsPerYear),
      };
    }
    case ConverterOptions.TODTO: {
      let yearsOfExperienceMapped = getLinearValueFromKeyIteration(yearsOfExperience, body[key].yearsOfExperience);
      yearsOfExperienceMapped = yearsOfExperienceMapped === 'ZERO' ? '0' : yearsOfExperienceMapped;
      return {
        [dtoKeyName]: body[key].hasValue,
        [`${dtoKeyName}Values`]: {
          yearsOfExperience: yearsOfExperienceMapped,
          transactionsPerYear: getLinearValueFromKeyIteration(transactionsPerYear, body[key].transactionsPerYear),
        },
      };
    }
    default:
      return undefined;
  }
};

/**
 * Two way mapper method handling the values of a checkbox group
 */
const getCheckboxGroupValue = (values, iteratableValues) => {
  switch (converterOption) {
    case ConverterOptions.TOVIEWMODEL: {
      return Object.keys(iteratableValues).map(key => (_.isEmpty(values) ? [key, false] : [key, values.includes(key)]));
    }
    case ConverterOptions.TODTO: {
      return Object.keys(values).filter(key => values[key]);
    }
    default:
      return undefined;
  }
};

/**
 * Single purpose method handling the values of the funding options group
 * @param {*} fundingOptions The funding options array to be converted the the effectively sent array
 */
const getFundingMethodsForDto = fundingOptions => Object.keys(fundingOptions).filter(key =>
  fundingOptions[key] && key in fundingMethodsMappings)
  .map(key => fundingMethodsMappings[key]);

/**
 * The maping method used to map from the dto returned from a API endpoint to the view model used in the underlying component
 * @param {*} suitabilityInfo The suitability information to be parsed
 */
export const toViewModel = (suitabilityInfo) => {
  converterOption = ConverterOptions.TOVIEWMODEL;
  const financialSituation = {
    taxRate: getLinearValueFromKeyIteration(taxRateValues, suitabilityInfo.taxRate),
    annualExpenses: getLinearValueFromKeyIteration(expensesValues, suitabilityInfo.annualExpenses),
    specialExpenses: getLinearValueFromKeyIteration(specialExpensesValues, suitabilityInfo.specialExpenses),
    timeFrame: getLinearValueFromKeyIteration(timeFrameForSpecialExpensesValues, suitabilityInfo.timeFrameForSpecialExpenses),
    liquidityNeeds: getLinearValueFromKeyIteration(liquidityNeedsValues, suitabilityInfo.liquidityNeeds),
  };

  const investmentRiskTolerance = {
    riskTolerance: getLinearValueFromKeyIteration(riskToleranceValues, suitabilityInfo.riskTolerance),
    investmentObjectives: getLinearValueFromKeyIteration(investmentObjectivesValues, suitabilityInfo.investmentObjectives),
  };

  const investmentExperience = {
    mutualFunds: getConditionalTupletValue(suitabilityInfo.mutualFunds, suitabilityInfo.mutualFundsValues)({ body: undefined, dtoKeyName: undefined }),
    commodities: getConditionalTupletValue(suitabilityInfo.commodities, suitabilityInfo.commoditiesValues)({ body: undefined, dtoKeyName: undefined }),
    individualStocks: getConditionalTupletValue(suitabilityInfo.individualStocks, suitabilityInfo.individualStocksValues)({ body: undefined, dtoKeyName: undefined }),
    bonds: getConditionalTupletValue(suitabilityInfo.bonds, suitabilityInfo.bondsValues)({ body: undefined, dtoKeyName: undefined }),
    options: getConditionalTupletValue(suitabilityInfo.options, suitabilityInfo.optionsValues)({ body: undefined, dtoKeyName: undefined }),
    securities: getConditionalTupletValue(suitabilityInfo.securitiesFutures, suitabilityInfo.securitiesFuturesValues)({ body: undefined, dtoKeyName: undefined }),
    annuities: getConditionalTupletValue(suitabilityInfo.annuities, suitabilityInfo.annuitiesValues)({ body: undefined, dtoKeyName: undefined }),
    alternative: getConditionalTupletValue(suitabilityInfo.alternatives, suitabilityInfo.alternativesValues)({ body: undefined, dtoKeyName: undefined }),
    margins: getConditionalTupletValue(suitabilityInfo.margin, suitabilityInfo.marginValues)({ body: undefined, dtoKeyName: undefined }),
    reits: getConditionalTupletValue(suitabilityInfo.REITs, suitabilityInfo.REITsValues)({ body: undefined, dtoKeyName: undefined }),
    decisionMaking: Object.fromEntries(getCheckboxGroupValue(suitabilityInfo.decisionMaking, decisionMakingOptions)),
  };

  const investmentObjectives = {
    investments: getLinearValueFromKeyIteration(investmentOptions, suitabilityInfo.investmentPortfolio),
    investmentTimespan: getLinearValueFromKeyIteration(investmentTimespanOptions, suitabilityInfo.timeHorizon),
    fundingOption: {
      income: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.income) >= 0,
      pension: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.pension) >= 0,
      otherFunds: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.otherFunds) >= 0,
      gift: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.gift) >= 0,
      saleOfBusiness: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.saleOfBusiness) >= 0,
      insurancePayout: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.insurancePayout) >= 0,
      inheritance: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.inheritance) >= 0,
      socialSecurityBenefits: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.socialSecurityBenefits) >= 0,
      reverseMortgage: suitabilityInfo.fundingMethods.indexOf(fundingMethodsMappings.reverseMortgage) >= 0,
      other: !_.isEmpty(suitabilityInfo.specialFundingMethod),
      otherTextValue: _.isEmpty(suitabilityInfo.specialFundingMethod) ? undefined : suitabilityInfo.specialFundingMethod,
    },
  };

  return {
    financialSituation,
    investmentRiskTolerance,
    investmentExperience,
    investmentObjectives,
  };
};

/**
 * The maping method used to map from the view model used in the underlying component to the dto returned from a API endpoint
 * @param {*} suitabilityInfo The suitability information to be parsed
 */
export const toDto = (suitabilityInfo) => {
  converterOption = ConverterOptions.TODTO;
  return {
    taxRate: getLinearValueFromKeyIteration(taxRateValues, suitabilityInfo.financialSituation.taxRate),
    annualExpenses: getLinearValueFromKeyIteration(expensesValues, suitabilityInfo.financialSituation.annualExpenses),
    specialExpenses: getLinearValueFromKeyIteration(specialExpensesValues, suitabilityInfo.financialSituation.specialExpenses),
    timeFrameForSpecialExpenses: getLinearValueFromKeyIteration(timeFrameForSpecialExpensesValues, suitabilityInfo.financialSituation.timeFrameSpecialExpenses),
    liquidityNeeds: getLinearValueFromKeyIteration(liquidityNeedsValues, suitabilityInfo.financialSituation.liquidityNeeds),
    riskTolerance: getLinearValueFromKeyIteration(riskToleranceValues, suitabilityInfo.investmentRiskTolerance.riskTolerance),
    investmentObjectives: getLinearValueFromKeyIteration(investmentObjectivesValues, suitabilityInfo.investmentRiskTolerance.investmentObjectives),
    ...getConditionalTupletValue('mutualFunds', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'mutualFunds' }),
    ...getConditionalTupletValue('commodities', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'commodities' }),
    ...getConditionalTupletValue('individualStocks', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'individualStocks' }),
    ...getConditionalTupletValue('bonds', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'bonds' }),
    ...getConditionalTupletValue('options', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'options' }),
    ...getConditionalTupletValue('securities', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'securitiesFutures' }),
    ...getConditionalTupletValue('annuities', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'annuities' }),
    ...getConditionalTupletValue('alternative', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'alternatives' }),
    ...getConditionalTupletValue('margins', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'margin' }),
    ...getConditionalTupletValue('reits', undefined)({ body: suitabilityInfo.investmentExperience, dtoKeyName: 'REITs' }),
    decisionMaking: getCheckboxGroupValue(suitabilityInfo.investmentExperience.decisionMaking, decisionMakingOptions),
    investmentPortfolio: getLinearValueFromKeyIteration(investmentOptions, suitabilityInfo.investmentObjectives.investments),
    timeHorizon: getLinearValueFromKeyIteration(investmentTimespanOptions, suitabilityInfo.investmentObjectives.investmentTimespan),
    fundingMethods: getFundingMethodsForDto(suitabilityInfo.investmentObjectives.fundingOption),
    specialFundingMethod: !_.isEmpty(suitabilityInfo.investmentObjectives.fundingOption.otherTextValue) ? suitabilityInfo.investmentObjectives.fundingOption.otherTextValue : '',
  };
};



// WEBPACK FOOTER //
// ./src/helpers/mappings/suitabilityMappings.js